import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Menu, X, ChevronDown } from 'lucide-react';
import { Law } from '../../Entities/Law';

const TopNavigation = ({ onMobileMenuToggle }) => {
  const location = useLocation();
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);
  
  const { data: laws = [] } = useQuery({
    queryKey: ['laws'],
    queryFn: () => Law.list()
  });

  // Generate search suggestions
  useEffect(() => {
    if (searchTerm.trim().length > 2 && laws.length > 0) {
      const newSuggestions = [];
      const term = searchTerm.toLowerCase();

      // Helper: extract a context snippet around the matched term
      const getSnippet = (text, maxLen = 120) => {
        if (!text) return '';
        const lower = text.toLowerCase();
        const idx = lower.indexOf(term);
        if (idx === -1) return '';
        // Find the start of the clause/point containing the match
        // Look backwards for common clause markers or sentence start
        let start = idx;
        const clausePattern = /(?:\n|^)\s*(?:\(\d+\)|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z])/g;
        let lastClause = 0;
        let m;
        const before = text.substring(0, idx);
        // Find last newline-based boundary
        const lastNewline = before.lastIndexOf('\n');
        if (lastNewline !== -1 && idx - lastNewline < 200) {
          start = lastNewline + 1;
        } else {
          start = Math.max(0, idx - 50);
        }
        let end = Math.min(text.length, start + maxLen);
        // Extend to end of word
        while (end < text.length && text[end] !== ' ' && text[end] !== '\n') end++;
        let snippet = text.substring(start, end).replace(/\s+/g, ' ').trim();
        if (start > 0) snippet = '...' + snippet;
        if (end < text.length) snippet = snippet + '...';
        return snippet;
      };

      // Helper: identify which clause/sub-section contains the match
      const getClauseLabel = (content) => {
        if (!content) return '';
        const lower = content.toLowerCase();
        const idx = lower.indexOf(term);
        if (idx === -1) return '';
        const before = content.substring(0, idx);
        // Find the last numbered clause like (1), (2), etc.
        const clauseMatches = [...before.matchAll(/\((\d+)\)/g)];
        const subClauseMatches = [...before.matchAll(/\(([a-z])\)/g)];
        let label = '';
        if (clauseMatches.length > 0) {
          label = `Sub-section (${clauseMatches[clauseMatches.length - 1][1]})`;
          if (subClauseMatches.length > 0) {
            const lastSub = subClauseMatches[subClauseMatches.length - 1][1];
            label += `(${lastSub})`;
          }
        }
        return label;
      };
      
      // Collect results per law so we can interleave them
      const resultsPerLaw = [];
      laws.forEach(law => {
        const lawResults = [];
        const isRules = law.id === 'dpdp-rules';
        const artLabel = isRules ? 'Rule' : 'Section';
        
        // Add law title matches
        if (law.title?.toLowerCase().includes(term) ||
            law.short_title?.toLowerCase().includes(term)) {
          lawResults.push({
            type: 'law',
            title: law.title,
            subtitle: law.short_title,
            snippet: '',
            path: `/law-detail?id=${law.id}`
          });
        }
        
        if (law.chapters && Array.isArray(law.chapters)) {
          law.chapters.forEach(chapter => {
            // Add article matches
            if (chapter.articles && Array.isArray(chapter.articles)) {
              chapter.articles.forEach(article => {
                const articleTitle = article.title || '';
                const articleContent = article.content || '';
                
                if (articleTitle.toLowerCase().includes(term) ||
                    articleContent.toLowerCase().includes(term)) {
                  const snippet = getSnippet(articleContent);
                  const clauseLabel = getClauseLabel(articleContent);
                  lawResults.push({
                    type: 'article',
                    title: `${artLabel} ${article.number}: ${articleTitle}`,
                    subtitle: `${law.short_title} - Chapter ${chapter.number}`,
                    snippet: snippet,
                    clauseLabel: clauseLabel,
                    path: `/law-detail?id=${law.id}&chapter=${chapter.number}&article=${article.number}`
                  });
                }
              });
            }
          });
        }

        // Search schedules
        if (law.schedules && Array.isArray(law.schedules)) {
          law.schedules.forEach(schedule => {
            const schedName = schedule.name || '';
            const schedTitle = schedule.title || '';
            const schedContent = schedule.content || '';
            const schedNote = schedule.note || '';
            const allText = `${schedName} ${schedTitle} ${schedContent} ${schedNote}`;

            // Check schedule-level text
            if (allText.toLowerCase().includes(term)) {
              const snippet = getSnippet(schedContent || schedNote || schedTitle);
              lawResults.push({
                type: 'schedule',
                title: schedule.name,
                subtitle: `${law.short_title}`,
                snippet: snippet,
                path: `/law-detail?id=${law.id}&schedule=${schedule.number}`
              });
            }

            // Check parts within schedule
            if (schedule.parts && Array.isArray(schedule.parts)) {
              schedule.parts.forEach(part => {
                const partTitle = part.title || '';
                const partContent = part.content || '';
                const partNote = part.note || '';
                const partPoints = part.points ? JSON.stringify(part.points) : '';
                const partAll = `${partTitle} ${partContent} ${partNote} ${partPoints}`;
                
                if (partAll.toLowerCase().includes(term)) {
                  const snippet = getSnippet(partContent || partNote || partTitle || partPoints);
                  lawResults.push({
                    type: 'schedule',
                    title: `${schedule.name} - Part ${part.part}`,
                    subtitle: `${law.short_title} - ${partTitle.substring(0, 60)}${partTitle.length > 60 ? '...' : ''}`,
                    snippet: snippet,
                    path: `/law-detail?id=${law.id}&schedule=${schedule.number}&part=${part.part}`
                  });
                }
              });
            }

            // Check table rows within schedule
            if (schedule.table && schedule.table.rows) {
              schedule.table.rows.forEach(row => {
                const rowText = Object.values(row).join(' ');
                if (rowText.toLowerCase().includes(term)) {
                  const snippet = getSnippet(rowText);
                  lawResults.push({
                    type: 'schedule',
                    title: `${schedule.name} - Row ${row.s_no}`,
                    subtitle: `${law.short_title}`,
                    snippet: snippet,
                    path: `/law-detail?id=${law.id}&schedule=${schedule.number}`
                  });
                }
              });
            }
          });
        }
        resultsPerLaw.push(lawResults);
      });

      // Group results by type (Act articles, Rules, Schedules) with section headers
      const actResults = [];
      const rulesResults = [];
      const scheduleResults = [];
      
      // Separate results by law type
      for (let idx = 0; idx < resultsPerLaw.length; idx++) {
        const results = resultsPerLaw[idx];
        const lawId = laws[idx]?.id;
        
        for (const result of results) {
          if (result.type === 'schedule') {
            scheduleResults.push(result);
          } else if (lawId === 'dpdp-rules') {
            // For rules, separate law title from articles
            if (result.type === 'law') {
              rulesResults.push(result);
            } else {
              rulesResults.push(result);
            }
          } else {
            // For act, separate law title from articles
            if (result.type === 'law') {
              actResults.push(result);
            } else {
              actResults.push(result);
            }
          }
        }
      }
      
      // Add results with section headers
      if (actResults.length > 0) {
        newSuggestions.push({ isHeader: true, type: 'section', title: 'DPDP ACT' });
        newSuggestions.push(...actResults);
      }
      
      if (rulesResults.length > 0) {
        newSuggestions.push({ isHeader: true, type: 'section', title: 'DPDP RULES' });
        newSuggestions.push(...rulesResults);
      }
      
      if (scheduleResults.length > 0) {
        newSuggestions.push({ isHeader: true, type: 'section', title: 'SCHEDULES' });
        newSuggestions.push(...scheduleResults);
      }
      
      setSuggestions(newSuggestions);
      setShowDropdown(true);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [searchTerm, laws]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/?search=${encodeURIComponent(searchTerm.trim())}`);
      setShowDropdown(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(suggestion.path);
    setSearchTerm('');
    setShowDropdown(false);
  };
  


  // Highlight search term in text
  const highlightTerm = (text) => {
    if (!text || !searchTerm.trim()) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="bg-yellow-200 text-black rounded px-0.5">{part}</mark> : part
    );
  };

  // Determine active tab
  const path = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const lawId = searchParams.get('id');
  const isActive = (tabPath) => {
    if (path === tabPath) return true;
    // When on /law-detail, highlight the correct tab based on lawType id
    if (path === '/law-detail') {
      if (tabPath === '/dpdp' && (!lawId || lawId === 'dpdp')) return true;
      if (tabPath === '/dpdp-rules' && lawId === 'dpdp-rules') return true;
    }
    return false;
  };

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-cyan-600 border-b border-blue-500 text-white shadow-sm z-50 w-full" style={{fontFamily: 'Inter, Arial, sans-serif'}}>
      <div className="w-full px-3 sm:px-6">
        <div className="flex items-center h-14 sm:h-16 w-full relative">
          {/* Left: Logo + Hamburger Menu */}
          <div className="flex items-center space-x-2">
            <Link to="/" className="flex items-center" aria-label="Home" title="Go to home">
              <img
                src="/logo.jpeg"
                alt="DataLaw.in logo"
                className="h-6 w-auto sm:h-8 md:h-10 lg:h-11 object-contain"
                style={{ maxWidth: '140px', minWidth: '60px' }}
              />
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-white/20 transition-colors"
              onClick={onMobileMenuToggle}
              aria-label="Toggle sidebar menu"
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Center: Navigation Items */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center">
            {/* Desktop: All items visible */}
            <div className="hidden sm:flex items-center space-x-6 md:space-x-12 whitespace-nowrap">
              <Link
                to="/dpdp"
                className={`text-sm md:text-base px-2 md:px-3 py-1 transition-colors duration-200 border-b-2 ${isActive('/dpdp') ? 'border-white text-white font-bold' : 'border-transparent text-white hover:font-bold'}`}
                style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400, color: '#fff'}}>
                DPDP ACT
              </Link>
              <Link
                to="/dpdp-rules"
                className={`text-sm md:text-base px-2 md:px-3 py-1 transition-colors duration-200 border-b-2 ${isActive('/dpdp-rules') ? 'border-white text-white font-bold' : 'border-transparent text-white hover:font-bold'}`}
                style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400, color: '#fff'}}>
                DPDP RULES
              </Link>
              <Link
                to="/blog"
                className={`text-sm md:text-base px-2 md:px-3 py-1 transition-colors duration-200 border-b-2 relative ${isActive('/blog') ? 'border-white text-white font-bold' : 'border-transparent text-white hover:font-bold'}`}
                style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400, color: '#fff'}}>
                BLOG
                <span className="absolute -top-1.5 -right-3" style={{filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))', animation: 'floatBubble 2s ease-in-out infinite'}}>
                  <span className="relative block bg-white text-blue-600 font-bold px-1 py-0.5 rounded-md" style={{fontSize: '7px', letterSpacing: '0.04em', lineHeight: 1}}>
                    SOON
                    <span className="absolute -bottom-1 left-2 w-0 h-0" style={{borderLeft: '3px solid transparent', borderRight: '3px solid transparent', borderTop: '3px solid white'}}></span>
                  </span>
                </span>
                <style>{`@keyframes floatBubble { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }`}</style>
              </Link>
              <Link
                to="/contact-us"
                className={`text-sm md:text-base px-2 md:px-3 py-1 transition-colors duration-200 border-b-2 ${isActive('/contact-us') ? 'border-white text-white font-bold' : 'border-transparent text-white hover:font-bold'}`}
                style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400, color: '#fff'}}>
                CONTACT US
              </Link>
            </div>

            {/* Mobile: Main items + dropdown */}
            <div className="flex sm:hidden items-center space-x-2 whitespace-nowrap">
              <Link
                to="/dpdp"
                className={`text-xs px-1 py-1 transition-colors duration-200 border-b-2 ${isActive('/dpdp') ? 'border-white text-white font-bold' : 'border-transparent text-white hover:font-bold'}`}
                style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400, color: '#fff'}}>
                ACTS
              </Link>
              <Link
                to="/dpdp-rules"
                className={`text-xs px-1 py-1 transition-colors duration-200 border-b-2 ${isActive('/dpdp-rules') ? 'border-white text-white font-bold' : 'border-transparent text-white hover:font-bold'}`}
                style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400, color: '#fff'}}>
                RULES
              </Link>
              <div className="relative">
                <button
                  onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                  className="flex items-center text-xs px-1 py-1 text-white hover:font-bold transition-colors duration-200 border-b-2 border-transparent"
                  style={{fontFamily: 'Inter, Arial, sans-serif', letterSpacing: '0.01em', fontWeight: 400}}
                >
                  MORE
                  <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${mobileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setMobileDropdownOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[140px]">
                      <Link
                        to="/blog"
                        onClick={() => setMobileDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors relative"
                      >
                        Blog
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded font-medium">
                          Soon
                        </span>
                      </Link>
                      <Link
                        to="/contact-us"
                        onClick={() => setMobileDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        Contact Us
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right: Search */}
          <div className="ml-auto flex items-center">
            {/* Search Icon - mobile */}
            <button
              className="xl:hidden p-2 rounded-md hover:bg-white/20 transition-colors ml-1"
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              aria-label="Toggle search"
            >
              {mobileSearchOpen ? <X className="w-5 h-5 text-white" /> : <Search className="w-5 h-5 text-white" />}
            </button>

          {/* Search Bar - Desktop */}
          <div className="hidden xl:flex items-center" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative w-80">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search laws and articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchTerm.length > 2 && setShowDropdown(true)}
                  className="w-full bg-white text-black placeholder-gray-400 border-2 border-white rounded-full pl-10 pr-4 py-1.5 text-sm focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-200 shadow-lg transition-all duration-200"
                  style={{fontFamily: 'Inter, Arial, sans-serif'}}
                />
              </div>
              {/* Dropdown Suggestions */}
              {showDropdown && searchTerm.trim().length > 2 && (
                <div className="absolute top-full right-0 mt-2 bg-white border-2 border-blue-100 rounded-lg shadow-xl z-50 max-h-[28rem] overflow-y-auto" style={{width: '420px', maxWidth: 'calc(100vw - 2rem)'}}>
                  {suggestions.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">
                      No results found for <span className="font-semibold text-gray-700">"{searchTerm}"</span>
                    </div>
                  ) : (
                  suggestions.map((suggestion, idx) => 
                    suggestion.isHeader ? (
                      <div key={idx} className="px-4 py-2 mt-2 mb-1 bg-gray-100 text-gray-700 font-bold text-xs uppercase tracking-wider border-b border-gray-200">
                        {suggestion.title}
                      </div>
                    ) : (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-blue-50 last:border-b-0 transition-colors duration-150"
                    >
                      {suggestion.clauseLabel && (
                        <div className="text-[10px] text-orange-600 font-medium mb-1">{suggestion.clauseLabel}</div>
                      )}
                      <div className="font-medium text-black text-sm">{highlightTerm(suggestion.title)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{suggestion.subtitle}</div>
                      {suggestion.snippet && (
                        <div className="text-xs text-gray-600 mt-1.5 leading-relaxed bg-gray-50 rounded px-2 py-1.5 border-l-2 border-blue-300">
                          {highlightTerm(suggestion.snippet)}
                        </div>
                      )}
                    </button>
                    )
                  ))}
                </div>
              )}
            </form>
          </div>
          </div>{/* end ml-auto right section */}
        </div>
      </div>

      {/* Mobile Search Bar - Expandable */}
      {mobileSearchOpen && (
        <div className="xl:hidden border-t border-blue-500 bg-gradient-to-r from-blue-600 to-cyan-600 px-3 py-2" ref={searchRef}>
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search laws and articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm.length > 2 && setShowDropdown(true)}
              className="w-full bg-white text-black placeholder-gray-400 border-2 border-white rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-200 shadow-lg"
              style={{fontFamily: 'Inter, Arial, sans-serif'}}
              autoFocus
            />
            {showDropdown && searchTerm.trim().length > 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-100 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                {suggestions.length === 0 ? (
                  <div className="px-4 py-6 text-center text-gray-500 text-sm">
                    No results found for <span className="font-semibold text-gray-700">"{searchTerm}"</span>
                  </div>
                ) : (
                suggestions.map((suggestion, idx) => 
                  suggestion.isHeader ? (
                    <div key={idx} className="px-4 py-2 mt-2 mb-1 bg-gray-100 text-gray-700 font-bold text-xs uppercase tracking-wider border-b border-gray-200">
                      {suggestion.title}
                    </div>
                  ) : (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => { handleSuggestionClick(suggestion); setMobileSearchOpen(false); }}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-blue-50 last:border-b-0 transition-colors duration-150"
                  >
                    {suggestion.clauseLabel && (
                      <div className="text-[10px] text-orange-600 font-medium mb-1">{suggestion.clauseLabel}</div>
                    )}
                    <div className="font-medium text-black text-sm">{highlightTerm(suggestion.title)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{suggestion.subtitle}</div>
                    {suggestion.snippet && (
                      <div className="text-xs text-gray-600 mt-1.5 leading-relaxed bg-gray-50 rounded px-2 py-1.5 border-l-2 border-blue-300">
                        {highlightTerm(suggestion.snippet)}
                      </div>
                    )}
                  </button>
                  )
                ))}
              </div>
            )}
          </form>
        </div>
      )}
    </nav>
  );
};

export default TopNavigation;
